// Pure selectors that turn query data into Fleet module face props.
// No React, no DOM: every function tolerates undefined/missing input and
// returns null/empty fields rather than throwing or leaking NaN.
import type {
  DrawdownFloorStatus,
  EquityHistory,
  SectorExposure,
  SectorShiftProposal,
} from '../../types'
import type { BenchmarkReturns, WorkflowRunRow } from '../../api/client'

const MINUS = '−'
const DASH = '—'

export const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/** `· 1 book unavailable` / `· 2 books unknown`; null when n is 0. */
export const bookCountMarker = (n: number, word: string): string | null =>
  n > 0 ? `· ${n} ${n === 1 ? 'book' : 'books'} ${word}` : null

// ---------------------------------------------------------------- formatters

export function fmtUsd(v: number): string {
  if (!Number.isFinite(v)) return DASH
  const r = Math.round(v)
  const body = `$${Math.abs(r).toLocaleString('en-US')}`
  return r < 0 ? `${MINUS}${body}` : body
}

export function fmtSignedUsd(v: number): string {
  if (!Number.isFinite(v)) return DASH
  const r = Math.round(v)
  if (r === 0) return '$0'
  const body = `$${Math.abs(r).toLocaleString('en-US')}`
  return r < 0 ? `${MINUS}${body}` : `+${body}`
}

export function fmtPct(v: number, dp = 2): string {
  if (!Number.isFinite(v)) return DASH
  const body = `${Math.abs(v).toFixed(dp)}%`
  // A value that rounds to zero carries no sign.
  if (Number(Math.abs(v).toFixed(dp)) === 0) return body
  return v < 0 ? `${MINUS}${body}` : `+${body}`
}

// ------------------------------------------------------------------- equity

/** Sum equity by date over dates present in EVERY provided history. */
export function sumEquitySeries(histories: Array<EquityHistory | undefined>): number[] {
  const present = histories.filter((h): h is EquityHistory => !!h && Array.isArray(h.points))
  if (present.length === 0) return []
  const maps = present.map((h) => {
    const m = new Map<string, number>()
    for (const p of h.points) {
      const e = num(p.equity)
      if (e !== null) m.set(p.t, e)
    }
    return m
  })
  const common = [...maps[0].keys()].filter((t) => maps.every((m) => m.has(t))).sort()
  if (common.length < 2) return []
  return common.map((t) => maps.reduce((s, m) => s + (m.get(t) as number), 0))
}

export function fleetEquity(
  accounts: Array<{ account_value: number | string; daily_pnl: number | string } | undefined>,
): { equity: number; pnl: number; pct: number | null; perBook: number[] } {
  let equity = 0
  let pnl = 0
  const perBook: number[] = []
  for (const a of accounts) {
    if (!a) continue
    const v = num(a.account_value) ?? 0
    equity += v
    pnl += num(a.daily_pnl) ?? 0
    perBook.push(v)
  }
  const base = equity - pnl
  return { equity, pnl, pct: base > 0 ? (pnl / base) * 100 : null, perBook }
}

// ------------------------------------------------------------------- excess

const HORIZONS = [
  ['day', '1D'],
  ['1w', '1W'],
  ['1m', '1M'],
  ['3m', '3M'],
] as const

export function excessByHorizon(
  bench: BenchmarkReturns | undefined,
  bookPct: Record<'day' | '1w' | '1m' | '3m', number | null>,
): Array<{ label: '1D' | '1W' | '1M' | '3M'; value: number | null; display: string }> {
  return HORIZONS.map(([key, label]) => {
    const book = num(bookPct?.[key])
    const b = num(bench?.windows?.[key]?.return_pct)
    const value = book === null || b === null ? null : book - b
    return { label, value, display: value === null ? DASH : fmtPct(value) }
  })
}

// ----------------------------------------------------------------- drawdown

export interface DrawdownPick {
  drawdown: number | null
  floor: number | null
  label: string | null
  /** The chosen book's floor is latched (operator-reset only). */
  tripped: boolean
  /** Every book whose floor is tripped, chosen or not. */
  trippedCount: number
  /** False when the chosen book's floor is disarmed (the card's "Off" pill). */
  enabled: boolean
  /** Live drawdown is null and the value is `last_known_drawdown` (market closed). */
  stale: boolean
}

/**
 * The drawdown-floor face reduces on RISK STATE, not on the largest drawdown:
 * the floor is a persisted latch that only the operator clears, so a tripped
 * book usually sits BELOW its floor (and below other books) after it trips.
 *  1. Any tripped book wins (least headroom among the tripped ones).
 *  2. Otherwise the book with the least headroom (`floor − drawdown`), since
 *     floors are per book (overlay | settings).
 */
export function worstDrawdown(
  statuses: Array<{ status?: DrawdownFloorStatus; label: string }>,
): DrawdownPick {
  const rows = (statuses ?? []).flatMap((s) => {
    if (!s?.status) return []
    const live = num(s.status.drawdown)
    const known = num(s.status.last_known_drawdown)
    return [{
      label: s.label,
      dd: live ?? known,
      floor: num(s.status.floor),
      stale: live === null && known !== null,
      tripped: !!s.status.tripped,
      enabled: s.status.enabled !== false,
    }]
  })
  const tripped = rows.filter((r) => r.tripped)
  const pool = tripped.length > 0 ? tripped : rows.filter((r) => r.dd !== null)
  const headroom = (r: (typeof rows)[number]) =>
    r.dd === null || r.floor === null ? Infinity : r.floor - r.dd
  let best: (typeof rows)[number] | null = null
  for (const r of pool) {
    if (best === null) {
      best = r
      continue
    }
    const hr = headroom(r)
    const hb = headroom(best)
    if (hr < hb || (hr === hb && (r.dd ?? -Infinity) > (best.dd ?? -Infinity))) best = r
  }
  if (!best) {
    return { drawdown: null, floor: null, label: null, tripped: false, trippedCount: 0, enabled: true, stale: false }
  }
  return {
    drawdown: best.dd,
    floor: best.floor,
    label: best.label,
    tripped: best.tripped,
    trippedCount: tripped.length,
    enabled: best.enabled,
    stale: best.stale,
  }
}

export interface DrawdownFace {
  /** The headline: the drawdown as a signed percent (`−6.2%`), or a dash. */
  value: string
  tone: 'loss' | 'warn' | 'neutral'
  /** The compare line, before the warn marker. */
  base: string
  /** Warn-toned tail of the compare line, or null. */
  marker: string | null
  hasData: boolean
  /** The footer gauge, or null with no drawdown or no floor to fill toward. */
  bar: {
    value: number
    max: number
    marker?: number
    tone: 'brand' | 'warn' | 'loss'
    ariaLabel: string
  } | null
}

/**
 * Copy, tone and gauge of a drawdown-floor tile from `worstDrawdown`'s pick and
 * the count of books whose drawdown could not be read. One source for the Fleet
 * tile (the worst of every live book) and the Book tile (the one book). The tile
 * value carries a minus (a loss); the gauge's label states the same drawdown as a
 * plain magnitude, so a screen reader never hears "+21.0%" for a loss.
 */
export function drawdownFace(pick: DrawdownPick, unknown: number): DrawdownFace {
  const { drawdown, floor, label, tripped, trippedCount, enabled, stale } = pick

  // loss (any floor tripped) > warn (a book unknown, the chosen floor off, or
  // ≥ half the floor) > neutral. The selector already puts a tripped book first.
  let tone: 'loss' | 'warn' | 'neutral' = 'neutral'
  if (tripped) tone = 'loss'
  else if (unknown > 0 || !enabled) tone = 'warn'
  else if (drawdown !== null && floor !== null && floor > 0 && drawdown / floor >= 0.5) tone = 'warn'

  const floorText = floor === null ? DASH : `${Math.round(floor * 1000) / 10}%`
  const hasData = drawdown !== null
  const marker = [
    !tripped && !enabled && label !== null ? '· floor off' : null,
    trippedCount > 1 ? bookCountMarker(trippedCount, 'tripped') : null,
    bookCountMarker(unknown, 'unknown'),
  ].filter(Boolean).join(' ') || null
  const staleText = stale ? ' · last known' : ''
  const base = tripped
    ? `tripped · ${label} · floor ${floorText}${staleText}`
    : hasData
      ? `floor ${floorText} · ${label}${staleText}`
      : 'no drawdown data'

  return {
    value: hasData ? fmtPct(-drawdown * 100, 1) : DASH,
    tone,
    base,
    marker,
    hasData,
    bar:
      hasData && floor !== null
        ? {
            value: drawdown,
            max: floor,
            ...(enabled ? { marker: floor } : {}),
            tone: tone === 'neutral' ? 'brand' : tone,
            ariaLabel: `${tripped ? 'tripped' : 'worst'} drawdown ${(drawdown * 100).toFixed(1)}% of ${floorText} floor${enabled ? '' : ' (off)'}`,
          }
        : null,
  }
}

// ----------------------------------------------------------------- exposure

export function exposure(
  positions: Array<{ market_value: number }> | undefined,
  account: { account_value: number | string; cash: number | string } | undefined,
): { ratio: number | null; cash: number | null } {
  const cash = num(account?.cash)
  const value = num(account?.account_value)
  if (!positions || value === null || value <= 0) return { ratio: null, cash }
  const gross = positions.reduce((s, p) => s + Math.abs(num(p.market_value) ?? 0), 0)
  return { ratio: gross / value, cash }
}

// --------------------------------------------------------------- run pips

export function runPips(runs: WorkflowRunRow[] | undefined, n = 16): Array<'ok' | 'fail' | 'empty'> {
  const ts = (r: WorkflowRunRow) => {
    const t = r.started_at ? Date.parse(r.started_at) : NaN
    return Number.isFinite(t) ? t : null
  }
  // Newest first; runs without a parseable start keep their given order.
  const ordered = [...(runs ?? [])].sort((a, b) => {
    const ta = ts(a)
    const tb = ts(b)
    return ta !== null && tb !== null ? tb - ta : 0
  })
  const pips: Array<'ok' | 'fail' | 'empty'> = ordered
    .slice(0, n)
    .map((r) => (r.success ? 'ok' : 'fail'))
  while (pips.length < n) pips.push('empty')
  return pips
}

// ------------------------------------------------------------------- funnel

// Labels copied from STATE_LABEL in components/LifecycleFunnel.tsx (lines 22-31).
const FUNNEL_LABELS = [
  ['discovered', 'Discovered'],
  ['incubating', 'Incubating'],
  ['ready_to_enter', 'Ready'],
  ['entered', 'Entered'],
  ['held', 'Held'],
] as const

export function funnelStages(
  byState: Record<string, number> | undefined,
): Array<{ label: string; count: number; tone?: 'warn' }> {
  const count = (k: string) => num(byState?.[k]) ?? 0
  return [
    ...FUNNEL_LABELS.map(([k, label]) => ({ label, count: count(k) })),
    { label: 'Exited', count: count('exited'), tone: 'warn' as const },
  ]
}

// ------------------------------------------------------------------ sectors

const MAX_SLICES = 8

/**
 * Top `maxSlices` groups by weight plus one `Other` slice folding the rest,
 * so the donut and its legend agree. The Fleet face passes 5 to fit the
 * fixed tier; the default keeps eight.
 */
export function sectorSlices(
  exposureData: SectorExposure | undefined,
  minFromWeight: number,
  maxSlices = MAX_SLICES,
): {
  slices: Array<{ label: string; value: number; color: string; shed: boolean }>
  dominant: { label: string; value: number } | null
} {
  const groups = [...(exposureData?.by_group ?? [])]
    .map((g) => ({ label: g.group, weight: num(g.weight) ?? 0 }))
    .sort((a, b) => b.weight - a.weight)
  const pct = (w: number) => Math.round(w * 1000) / 10
  const slices: Array<{ label: string; value: number; color: string; shed: boolean }> = groups
    .slice(0, maxSlices)
    .map((g, i) => ({
      label: g.label,
      value: pct(g.weight),
      color: `var(--series-${i + 1})`,
      shed: g.weight >= minFromWeight,
    }))
  const rest = groups.slice(maxSlices)
  if (rest.length > 0) {
    slices.push({
      label: 'Other',
      value: pct(rest.reduce((s, g) => s + g.weight, 0)),
      color: 'var(--fg-subtle)',
      shed: false,
    })
  }
  const first = slices[0]
  return { slices, dominant: first ? { label: first.label, value: first.value } : null }
}

export function pendingProposals(p: SectorShiftProposal[] | undefined): number {
  return (p ?? []).filter((x) => x.status === 'pending').length
}

// -------------------------------------------------------------------- fills

export function todayFills(fills: Array<{ executed_at: string }> | undefined, now: Date): number {
  const today = now.toLocaleDateString('en-CA')
  let n = 0
  for (const f of fills ?? []) {
    const d = new Date(f.executed_at)
    if (Number.isNaN(d.getTime())) continue
    if (d.toLocaleDateString('en-CA') === today) n += 1
  }
  return n
}
